import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CirclePlus, MoreHorizontal, Settings } from "lucide-react";
import { useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  DESTINATIONS,
  DESTINATIONS_BY_ID,
  DEVELOPER_DESTINATIONS,
  SIMPLE_RAIL,
  type Destination,
} from "@/app/destinations";
import { Tooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/cn";
import { useDeveloperMode, useUiMode } from "@/lib/uiMode";
import { useRailOverflow } from "./useRailOverflow";

/** A stable empty list, so the overflow hook does not re-measure on every render. */
const NO_DESTINATIONS: Destination[] = [];

/**
 * Frame 2 of UI-3 — 60 px, icon only.
 *
 * The hairline on the right is drawn only while the navigator panel is open, per the
 * handoff: with the panel collapsed there is nothing on the other side of it to divide.
 */
export function DestinationRail({
  panelOpen,
  setupIncomplete,
  reviewCount,
}: {
  panelOpen: boolean;
  setupIncomplete: boolean;
  /**
   * How much is waiting, drawn as a badge on the queue button. Which number this is depends
   * on the mode, and the caller decides: full mode's Review queue holds two kinds, simple
   * mode's Needs you merges four, and each badge promises what its own screen has.
   */
  reviewCount: number;
}) {
  const mode = useUiMode();
  const developerMode = useDeveloperMode();
  const location = useLocation();
  const navigate = useNavigate();

  // UI-16 rewrites the rail: four destinations, two of them renamed, and no overflow —
  // four buttons fit any window that is not already a phone, so the "More" popover has
  // nothing to hold and is not drawn.
  const simple = mode === "simple";

  // UI-24 — appended, never spliced in: `DESTINATIONS` stays the list the cold-load and
  // rail-overflow tests enumerate with developer mode off, and this is the one place a
  // developer destination becomes visible at all. Never drawn in simple mode, which has no
  // overflow list to append to and no developer surfaces of its own.
  const full = useMemo(() => {
    if (!developerMode) return DESTINATIONS;

    const last = DESTINATIONS[DESTINATIONS.length - 1];
    if (!last) return DEVELOPER_DESTINATIONS;

    return [...DESTINATIONS.slice(0, -1), { ...last, separatorAfter: true }, ...DEVELOPER_DESTINATIONS];
  }, [developerMode]);

  const overflow = useRailOverflow(simple ? NO_DESTINATIONS : full);
  const containerRef = overflow.containerRef;
  const visible = simple ? [] : overflow.visible;
  const hidden = simple ? [] : overflow.hidden;

  const onCapturePage = location.pathname.startsWith("/add");
  const hiddenActive = hidden.some((d) => location.pathname.startsWith(d.path));
  const settingsActive = location.pathname.startsWith("/settings");

  return (
    <nav
      aria-label="Destinations"
      className="relative flex w-[60px] shrink-0 flex-col items-center bg-[var(--sidebar)] py-[var(--space-3)]"
    >
      {panelOpen && (
        <span
          aria-hidden
          className="absolute right-0 top-6 bottom-6 w-px bg-[var(--border-subtle)]"
        />
      )}

      <Tooltip label="Add a source" side="right">
        <button
          type="button"
          // Full mode's + goes straight to the audio route, which is the one the handoff
          // says it opens; simple mode has one capture page and no routes to choose
          // between, so it goes there.
          onClick={() => navigate(simple ? "/add" : "/add/audio")}
          aria-label="Add a source"
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] transition-all duration-[var(--duration-fast)]",
            onCapturePage ? "bg-[var(--sidebar-accent)]" : "hover:bg-[var(--sidebar-accent)]",
          )}
        >
          <CirclePlus size={20} strokeWidth={2} style={{ color: "var(--vl-life-soft)" }} />
        </button>
      </Tooltip>

      {/* The measured region. Everything above and below it is reserved space, which
          is what makes the overflow budget a real measurement rather than a guess. */}
      <div ref={containerRef} className="flex min-h-0 w-full flex-1 flex-col items-center gap-0 py-[var(--space-3)]">
        {simple &&
          SIMPLE_RAIL.map((entry) => {
            const destination = DESTINATIONS_BY_ID.get(entry.id)!;

            return (
              <div key={entry.id} className="contents">
                <RailButton
                  destination={destination}
                  label={entry.label}
                  badge={destination.id === "review" ? reviewCount : 0}
                />
                {entry.separatorAfter && (
                  <span aria-hidden className="my-2 h-px w-6 shrink-0 bg-[var(--border-subtle)]" />
                )}
              </div>
            );
          })}

        {visible.map((entry) =>
          entry.kind === "separator" ? (
            <span key={entry.key} aria-hidden className="my-2 h-px w-6 shrink-0 bg-[var(--border-subtle)]" />
          ) : (
            <RailButton
              key={entry.key}
              destination={entry.destination!}
              badge={entry.destination!.id === "review" ? reviewCount : 0}
            />
          ),
        )}

        {hidden.length > 0 && <MoreButton hidden={hidden} active={hiddenActive} />}
      </div>

      <Tooltip label="Settings" side="right">
        <NavLink
          to="/settings"
          aria-label="Settings"
          className={cn(
            "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] transition-all duration-[var(--duration-fast)]",
            settingsActive
              ? "bg-[var(--sidebar-accent)] text-[var(--text-primary)]"
              : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--text-primary)]",
          )}
        >
          <Settings size={20} strokeWidth={2} />
          {setupIncomplete && (
            <span
              aria-label="Setup is not finished"
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-[var(--radius-full)] ring-2"
              style={{ background: "var(--warning)", ["--tw-ring-color" as string]: "var(--sidebar)" }}
            />
          )}
        </NavLink>
      </Tooltip>
    </nav>
  );
}

function RailButton({
  destination,
  badge,
  label,
}: {
  destination: Destination;
  badge: number;
  /** What this destination is called here — simple mode renames two of them (UI-16). */
  label?: string;
}) {
  const Icon = destination.icon;
  const active = useIsActive(destination.path);
  const name = label ?? destination.label;

  return (
    <Tooltip label={name} side="right">
      {/* The className is a plain string, not NavLink's function form, and that is
          load-bearing: Radix's `asChild` clones this element through Slot, which merges
          className by joining strings. Handed a function it joins the function's source
          text, and the button renders with no styling at all. */}
      <NavLink
        to={destination.path}
        aria-label={name}
        className={cn(
          "relative my-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]",
          "transition-[transform,filter,background-color] duration-[var(--duration-fast)]",
          "hover:-translate-y-0.5 hover:scale-110 hover:brightness-[1.35] active:scale-95",
          active
            ? "bg-[var(--sidebar-accent)] text-[var(--text-primary)]"
            : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--text-primary)]",
        )}
        style={{ transitionTimingFunction: "var(--easing-bounce)" }}
      >
        {active && (
          <span
            aria-hidden
            className="absolute h-5 w-[3px] rounded-r-[var(--radius-sm)]"
            style={{ left: -10, background: "var(--primary)" }}
          />
        )}
        <Icon size={20} strokeWidth={2} />
        {destination.badged && badge > 0 && (
          <span
            aria-label={`${badge} waiting`}
            className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-[var(--radius-full)] px-1 text-[length:var(--text-2xs)] font-bold leading-none text-[var(--text-inverse)] ring-2"
            style={{ background: "var(--vl-accent-warm)", ["--tw-ring-color" as string]: "var(--sidebar)" }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </NavLink>
    </Tooltip>
  );
}

/** Matches a destination and anything beneath it, so detail routes keep the rail lit. */
function useIsActive(path: string): boolean {
  const { pathname } = useLocation();
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** The overflow popover. Hidden destinations keep their icons, labels and active state. */
function MoreButton({ hidden, active }: { hidden: Destination[]; active: boolean }) {
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label="More destinations"
          className={cn(
            "my-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)]",
            active
              ? "bg-[var(--sidebar-accent)] text-[var(--text-primary)]"
              : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--text-primary)]",
          )}
        >
          <MoreHorizontal size={20} strokeWidth={2} />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="right"
          align="start"
          sideOffset={8}
          className="z-[150] w-[200px] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-3)] p-1 shadow-[var(--shadow-lg)]"
        >
          {hidden.map((destination) => {
            const Icon = destination.icon;
            return (
              <NavLink
                key={destination.id}
                to={destination.path}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-[var(--space-2)] rounded-[var(--radius-md)] px-2 py-1.5 text-[length:var(--text-xs)]",
                    isActive
                      ? "bg-[var(--sidebar-accent)] font-semibold text-[var(--text-primary)]"
                      : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--text-primary)]",
                  )
                }
              >
                <Icon size={14} strokeWidth={2} />
                <span className="truncate">{destination.label}</span>
              </NavLink>
            );
          })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
